import React, { Fragment, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronRightIcon, LockIcon, PlusIcon } from 'lucide-react';
import { ruleTypeOrder } from '../../data/compPlan';
import { AuditEntry, CompRule, PlanVersion } from '../../types/commission';
import { formatRuleBound, formatRuleValue } from '../../utils/commission';
import { Badge } from '../sf/Badge';
import { Button } from '../sf/Button';
import { ExampleDataBadge } from '../sf/ExampleDataBadge';
import { NewBadge } from '../sf/NewBadge';
import { Td, Th } from '../sf/DataTable';
import { ScopeBadge } from '../sf/ScopeBadge';
import { Tooltip } from '../sf/Tooltip';
import { EditableCell } from './EditableCell';
import { EditableField, cellKey, useRulesTable } from './useRulesTable';
interface RulesTableProps {
  version: PlanVersion;
  editable: boolean;
  readOnlyReason: string;
  editor: string;
  onAudit: (entries: AuditEntry[]) => void;
}
const COLUMN_COUNT = 10;
export function RulesTable({
  version,
  editable,
  readOnlyReason,
  editor,
  onAudit
}: RulesTableProps) {
  const table = useRulesTable(version.rules, version.version, editor);
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  function handleSave() {
    if (!reason.trim()) {
      setReasonMissing(true);
      return;
    }
    const count = table.dirtyCount;
    const entries = table.save(reason.trim());
    onAudit(entries);
    setReason('');
    setReasonMissing(false);
    toast.success(`Saved ${count} rule ${count === 1 ? 'change' : 'changes'} to v${version.version} Draft`, {
      description: `${entries.length} field ${entries.length === 1 ? 'change' : 'changes'} written to the audit log.`
    });
  }
  function handleCancel() {
    table.cancel();
    setReason('');
    setReasonMissing(false);
  }
  const renderValue = (rule: CompRule) => rule.value === null && rule.sourceNote ? <span className="whitespace-nowrap text-xs text-sf-weak" data-id="element-146" data-mp="3tc02v0umfkbl">{rule.sourceNote}</span> : rule.value === null ? <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap" data-id="element-147" data-mp="3oceu5ypnfxlo">
        <Badge tone="warning" data-id="element-148" data-mp="3jlyh4fpqqdzu">Pending confirmation</Badge>
        <span className="font-semibold text-sf-amber-text" data-id="element-149" data-mp="3jkdlsnvlqr0x">{formatRuleValue(rule)}</span>
      </span> : formatRuleValue(rule);
  const renderEditable = (rule: CompRule, field: EditableField, display: React.ReactNode) => {
    const key = cellKey(rule.id, field);
    const cellEditable = editable && (field === 'value' || rule.basis !== 'none');
    return <Td align="right" className={`${table.editedCells.has(key) ? 'bg-sf-edited' : ''} ${cellEditable ? 'py-1' : ''}`} data-id="element-150" data-mp="30o3xl7qzkk37">
        <EditableCell display={display} rawValue={rule[field]} label={`${field} for ${rule.ruleKey}`} editable={cellEditable} isEditing={table.editingCell === key} onStart={() => table.setEditingCell(key)} onCommit={raw => table.commitCell(rule.id, field, raw)} data-id="element-151" data-mp="2wnk0u5abrihl" />
      </Td>;
  };
  return <div className="overflow-hidden rounded border border-sf-border" data-id="element-152" data-mp="2hpsp25tkn50n">
      <div className="overflow-x-auto" data-id="element-153" data-mp="2mhaga25slr8f">
        <table className="w-full border-collapse" data-id="element-154" data-mp="2mic291ocjy4x">
          <thead data-id="element-155" data-mp="2qjxauatulq9b">
            <tr data-id="element-156" data-mp="2qicfwk3mg5ax">
              <Th data-id="element-157" data-mp="2qi45qzyvve3f">Rule Key</Th>
              <Th data-id="element-158" data-mp="2ql8emanamy0f">Scope</Th>
              <Th data-id="element-159" data-mp="1ttobwroqqiki">Branch / Comp Type</Th>
              <Th data-id="element-160" data-mp="1tupmyw5rb528">Employee</Th>
              <Th data-id="element-161" data-mp="1tvhm2gykbkx9">Applies To</Th>
              <Th align="right" data-id="element-162" data-mp="1yrpxbj9n2tz5">Min</Th>
              <Th align="right" data-id="element-163" data-mp="1ysrf06l5uepu">Max</Th>
              <Th align="right" data-id="element-164" data-mp="22u2z5wl9m7qn">
                <span className="inline-flex items-center gap-1" data-id="element-165" data-mp="22srulujzwvyh">
                  {!editable && <LockIcon className="h-3 w-3" aria-label="Read-only" data-id="element-166" data-mp="27tfmsjjyniua" />}
                  Value
                </span>
              </Th>
              <Th data-id="element-167" data-mp="3e8ggsjpfdqy8">Unit</Th>
              <Th data-id="element-168" data-mp="3ebcfwbd0elo9">Last Modified</Th>
            </tr>
          </thead>
          <tbody data-id="element-169" data-mp="3j6i2zdi5e6dx">
            {ruleTypeOrder.map(type => {
            const rows = table.rules.filter(rule => rule.ruleType === type);
            const isCollapsed = table.collapsed.has(type);
            return <Fragment key={type} data-id="element-170" data-mp="3cu5qv9jmq3fb">
                  <tr data-id="element-171" data-mp="3qru909mbawgo">
                    <td colSpan={COLUMN_COUNT} className="border-b border-sf-border bg-[#FAFAF9] px-2 py-1.5" data-id="element-172" data-mp="3qt5ecdveoc9w">
                      <div className="flex items-center justify-between gap-3" data-id="element-173" data-mp="3mopb6kd6fiyd">
                        <button type="button" onClick={() => table.toggleGroup(type)} aria-expanded={!isCollapsed} className="flex items-center gap-1.5 rounded px-1 py-0.5 text-[13px] font-semibold text-sf-text transition-colors duration-150 ease-out hover:bg-sf-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-174" data-mp="4j2rnswrr0oo">
                          <ChevronRightIcon className={`h-4 w-4 text-sf-weak transition-transform duration-150 ease-out ${isCollapsed ? '' : 'rotate-90'}`} aria-hidden="true" data-id="element-175" data-mp="ick12kvqqx0z" />
                          {type}
                          <span className="font-normal text-sf-weak" data-id="element-176" data-mp="16vm2u8zlswdq">({rows.length})</span>
                        </button>
                        {editable ? <Button size="sm" icon={<PlusIcon data-id="element-178" data-mp="wxy477yvn4qa" />} onClick={() => table.addRule(type)} data-id="element-177" data-mp="wyhcdwyq6er4">
                            Add Rule
                          </Button> : <Tooltip content={readOnlyReason} align="end" data-id="element-179" data-mp="nxt29dcgcyji">
                            <Button size="sm" icon={<LockIcon data-id="element-181" data-mp="98wr37f3n5eo" />} disabled data-id="element-180" data-mp="nzw18mvl220d">
                              Add Rule
                            </Button>
                          </Tooltip>}
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed && rows.length === 0 && <tr data-id="element-182" data-mp="57l130sbci5y">
                      <td colSpan={COLUMN_COUNT} className="border-b border-sf-border px-3 py-2 pl-9 text-xs text-sf-weak" data-id="element-183" data-mp="5608vhth1bb9">
                        {version.groupNotes?.[type] ?? 'No rules in this version.'}
                      </td>
                    </tr>}
                  {!isCollapsed && rows.length > 0 && version.groupNotes?.[type] && <tr data-id="element-184" data-mp="1t5m3981vnq4u">
                      <td colSpan={COLUMN_COUNT} className="border-b border-sf-border bg-[#FAFAF9] px-3 py-1.5 pl-9 text-xs text-sf-weak" data-id="element-185" data-mp="1t6dopq1rg7he">
                        {version.groupNotes[type]}
                      </td>
                    </tr>}
                  {!isCollapsed && rows.map(rule => <tr key={rule.id} data-id="element-186" data-mp="2gwhrjv74k37c">
                        <Td className="whitespace-nowrap pl-9 font-mono text-xs text-sf-text" data-id="element-187" data-mp="2bwd7xwax6scw">{rule.ruleKey}</Td>
                        <Td data-id="element-188" data-mp="15gjg5koh552y">
                          <ScopeBadge scope={rule.scope} data-id="element-189" data-mp="15df1ftdduqy1" />
                        </Td>
                        <Td className="whitespace-nowrap text-sf-weak" data-id="element-190" data-mp="10eu35zhoyhc8">{rule.branch ?? rule.compType ?? '—'}</Td>
                        <Td className="whitespace-nowrap text-sf-weak" data-id="element-191" data-mp="3c1nrxclzmkt2">{rule.employee ?? '—'}</Td>
                        <Td className="min-w-[180px] text-xs text-sf-weak" data-id="element-192" data-mp="3h0qxcy5vqt1k">
                          {rule.proposed && <span className="mb-1 block" data-id="element-193" data-mp="3pv5b2warhvik">
                              <NewBadge label="NEW · v2 proposed, not in main" data-id="element-194" data-mp="3pwq4gilcb8eu" />
                            </span>}
                          {rule.description}
                          {rule.exampleData && <span className="mt-1 block" data-id="element-195" data-mp="8n1sjnfovkvc">
                              <ExampleDataBadge data-id="element-196" data-mp="8njmueisjp0m" />
                            </span>}
                        </Td>
                        {renderEditable(rule, 'min', formatRuleBound(rule, 'min'))}
                        {renderEditable(rule, 'max', formatRuleBound(rule, 'max'))}
                        {renderEditable(rule, 'value', renderValue(rule))}
                        <Td className="text-sf-weak" data-id="element-197" data-mp="1vipzzzsp1jzc">{rule.unit}</Td>
                        <Td className="whitespace-nowrap text-xs text-sf-weak" data-id="element-198" data-mp="1qkwvdb71gy16">
                          {rule.lastModifiedOn ? <>
                              <span className="text-sf-text" data-id="element-199" data-mp="24e5vsmd0e97k">{rule.lastModifiedBy}</span> · {rule.lastModifiedOn}
                            </> : rule.id.startsWith('new-') ? <span className="italic" data-id="element-200" data-mp="1cmqdv5medano">Unsaved</span> : '—'}
                        </Td>
                      </tr>)}
                </Fragment>;
          })}
            <tr data-id="element-201" data-mp="1golee7wi7k0e">
              <td colSpan={COLUMN_COUNT} className="border-b border-sf-border bg-sf-amber-light px-3 py-1.5 text-[13px] font-semibold text-sf-amber-text" data-id="element-202" data-mp="1gotojs18sb7w">
                Requested — not applied
              </td>
            </tr>
            {version.pending.map(item => <tr key={item.ruleKey} data-id="element-203" data-mp="30hd2r9lrep0c">
                <Td className="whitespace-nowrap pl-9 font-mono text-xs" data-id="element-204" data-mp="34k01lugula71">{item.ruleKey}</Td>
                <Td colSpan={4} className="text-[13px]" data-id="element-205" data-mp="2qkqion19c1zd">
                  <span className="font-semibold" data-id="element-206" data-mp="2qm1pkvq01pup">{item.label}</span>
                  <span className="block text-xs text-sf-weak" data-id="element-207" data-mp="2vis4hfq2ifej">{item.detail}</span>
                </Td>
                <Td colSpan={5} align="right" data-id="element-208" data-mp="3ja7779lrayqb">
                  <span className="inline-flex items-center gap-1.5" data-id="element-209" data-mp="3jbibrbn10aih">
                    <NewBadge label="NEW / requested" data-id="element-210" data-mp="3o7pktdapy24n" />
                    <Badge tone="neutral" data-id="element-211" data-mp="39juq5rd5hnox">Not applied</Badge>
                  </span>
                </Td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {table.error && <p className="border-t border-sf-border bg-sf-red-light px-4 py-2 text-xs text-sf-red" data-id="element-212" data-mp="62vyow7dqx0f">{table.error}</p>}
      <AnimatePresence initial={false} data-id="element-213" data-mp="atm6eg3a3bya">
        {editable && table.dirtyCount > 0 && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-wrap items-center gap-3 border-t border-sf-border bg-sf-bg px-4 py-2.5" data-id="element-214" data-mp="czfgazbj9oa3">
            <p className="text-[13px] text-sf-text" data-id="element-215" data-mp="8x0v48ke6cfn">
              <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm border border-[#E0E39A] bg-sf-edited align-middle" aria-hidden="true" data-id="element-216" data-mp="3qf3ohjxgrtd9" />
              {table.dirtyCount} unsaved {table.dirtyCount === 1 ? 'rule' : 'rules'}
            </p>
            <div className="ml-auto flex items-start gap-2" data-id="element-217" data-mp="3hfgngz8ncpkq">
              <div data-id="element-218" data-mp="3lf8w1by1ivq3">
                <label htmlFor={`reason-${version.version}`} className="sr-only" data-id="element-219" data-mp="3lgscb2jp56rv">
                  Reason for change (required)
                </label>
                <input id={`reason-${version.version}`} value={reason} onChange={event => {
              setReason(event.target.value);
              if (event.target.value.trim()) setReasonMissing(false);
            }} placeholder="Reason for change (required)" aria-invalid={reasonMissing} className={`h-8 w-[300px] rounded border bg-white px-3 text-[13px] focus:outline-none focus:ring-1 ${reasonMissing ? 'border-sf-red focus:ring-sf-red' : 'border-sf-border-strong focus:border-sf-blue focus:ring-sf-blue'}`} data-id="element-220" data-mp="37ibyysrbwi0r" />
                {reasonMissing && <p className="mt-1 text-xs text-sf-red" data-id="element-221" data-mp="3lhofitieqqsv">Add a reason — it’s saved to the audit log.</p>}
              </div>
              <Button onClick={handleCancel} data-id="element-222" data-mp="3gjwen9zsbucj">Cancel</Button>
              <Button variant="brand" onClick={handleSave} data-id="element-223" data-mp="3uimdov66043j">
                Save
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}